/**
 * Case Card Component (Presentation)
 *
 * Individual case card in the queue panel.
 */

import { forwardRef, useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';

import type { DraggableAttributes } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';

import { CallStatusIndicator } from './CallStatusIndicator';

import type { CallStatus } from '../../domain/types';

interface CaseCardProps {
  caseId: string;
  hirerName: string;
  tier: string;
  overdueAmount: number;
  hasPTP?: boolean;
  ptpCount?: number;
  isActive?: boolean;
  isDragging?: boolean;
  isActiveCall?: boolean; // Whether this is the currently active call (in progress)
  status?: CallStatus; // Call status from Socket.IO events
  onClick?: () => void;
  onMoveToTop?: () => void;
  onDelete?: () => void;
  // Drag and drop props
  dragListeners?: SyntheticListenerMap | undefined;
  dragAttributes?: DraggableAttributes | undefined;
  style?: React.CSSProperties;
  sx?: SxProps<Theme>;
}

export const CaseCard = forwardRef<HTMLButtonElement, CaseCardProps>(
  function CaseCard(
    {
      hirerName,
      tier,
      overdueAmount,
      hasPTP = false,
      ptpCount = 0,
      isActive = false,
      isDragging = false,
      isActiveCall = false,
      status,
      onClick,
      onMoveToTop,
      onDelete,
      dragListeners,
      dragAttributes,
      style,
      sx,
    },
    ref
  ) {
    const theme = useTheme();
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const menuOpen = Boolean(menuAnchor);

    const handleMenuOpen = useCallback((e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      setMenuAnchor(e.currentTarget);
    }, []);

    const handleMenuClose = useCallback(() => {
      setMenuAnchor(null);
    }, []);

    const handleMoveToTop = useCallback(() => {
      handleMenuClose();
      onMoveToTop?.();
    }, [handleMenuClose, onMoveToTop]);

    const handleDeleteClick = useCallback(() => {
      handleMenuClose();
      setDeleteDialogOpen(true);
    }, [handleMenuClose]);

    const handleDeleteCancel = useCallback(() => {
      setDeleteDialogOpen(false);
    }, []);

    const handleDeleteConfirm = useCallback(() => {
      setDeleteDialogOpen(false);
      onDelete?.();
    }, [onDelete]);

    // Build optional style prop
    const styleProps = style ? { style } : {};

    return (
      <ButtonBase
        ref={ref}
        onClick={onClick}
        {...styleProps}
        sx={{
          display: 'flex',
          width: '100%',
          textAlign: 'left',
          p: 1.5,
          bgcolor: isActive
            ? alpha(theme.palette.primary.main, 0.08)
            : 'background.default',
          border: 1,
          borderColor: isActive ? 'primary.main' : 'divider',
          borderRadius: 2,
          transition: 'all 0.2s',
          opacity: isDragging ? 0.5 : 1,
          '&:hover': {
            bgcolor: isActive
              ? alpha(theme.palette.primary.main, 0.12)
              : 'action.hover',
          },
          ...(sx as Record<string, unknown>),
        }}
      >
        {/* Drag Handle */}
        <Box
          component="span"
          {...dragListeners}
          {...dragAttributes}
          sx={{
            color: 'text.disabled',
            mr: 1,
            cursor: isDragging ? 'grabbing' : 'grab',
            touchAction: 'none',
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Chip
              label={tier}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.625rem',
                fontWeight: 700,
                bgcolor: alpha(theme.palette.secondary.main, 0.15),
                color: 'secondary.main',
              }}
            />
            {hasPTP && (
              <Chip
                label={`PTP${ptpCount > 1 ? ` +${String(ptpCount)}` : ''}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.625rem',
                  bgcolor: 'transparent',
                  border: 1,
                  borderColor: 'divider',
                }}
              />
            )}
            {/* Menu trigger */}
            <Box
              component="span"
              role="button"
              tabIndex={0}
              aria-controls={menuOpen ? 'case-card-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={menuOpen ? 'true' : undefined}
              sx={{
                ml: 'auto',
                p: 0.5,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                cursor: 'pointer',
                color: 'action.active',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
              }}
              onClick={handleMenuOpen}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  handleMenuOpen(e as unknown as React.MouseEvent<HTMLElement>);
                }
              }}
            >
              <MoreVertIcon fontSize="small" />
            </Box>
          </Box>

          <Typography
            variant="body2"
            fontWeight={600}
            noWrap
            sx={{ mb: 0.5 }}
          >
            {hirerName}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="error.main" fontWeight={600}>
              ${overdueAmount} Overdue
            </Typography>
            {status && (
              <CallStatusIndicator status={status} size="small" />
            )}
          </Box>
        </Box>

        {/* Dropdown Menu */}
        <Menu
          id="case-card-menu"
          anchorEl={menuAnchor}
          open={menuOpen}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          slotProps={{
            paper: {
              sx: {
                minWidth: 160,
              },
            },
          }}
        >
          <MenuItem
            onClick={handleMoveToTop}
            disabled={isActiveCall || !onMoveToTop}
          >
            <ListItemIcon>
              <VerticalAlignTopIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Move to Top</ListItemText>
          </MenuItem>
          <MenuItem
            onClick={handleDeleteClick}
            disabled={isActiveCall || !onDelete}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteOutlineIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        </Menu>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          onClick={(e) => e.stopPropagation()}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            Remove from Queue
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to remove <strong>{hirerName}</strong> from the queue?
              This action cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="inherit">
              Cancel
            </Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </ButtonBase>
    );
  }
);


